import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authAxios, API, useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "wa_active_workspace";
const Ctx = createContext({
  workspaces: [], active: null, max: 1, loading: true,
  refresh: async () => {}, switchTo: () => {}, createOne: async () => {},
  rename: async () => {}, remove: async () => {},
});

export function WorkspaceProvider({ children }) {
  const { user, permissions, loading: authLoading } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  });
  const [max, setMax] = useState(1);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setWorkspaces([]); setLoading(false); return; }
    try {
      const { data } = await authAxios.get(`${API}/workspaces`);
      const items = data.items || [];
      setWorkspaces(items);
      setMax(data.max_workspaces || 1);
      // If no active, OR active is no longer in list → fallback to first
      const stillExists = items.some((w) => w.id === activeId);
      const next = stillExists ? activeId : (items[0]?.id || "");
      if (next !== activeId) {
        setActiveId(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
      }
    } catch (e) {
      console.error("[workspaces] load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user, activeId]);

  // Load on auth ready
  useEffect(() => {
    if (authLoading) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  const switchTo = useCallback((wid) => {
    setActiveId(wid);
    try { localStorage.setItem(STORAGE_KEY, wid); } catch { /* ignore */ }
    // Force a hard reload so cached data on every page is refreshed under the new workspace.
    window.location.reload();
  }, []);

  const createOne = useCallback(async (name) => {
    const { data } = await authAxios.post(`${API}/workspaces`, { name });
    await refresh();
    return data;
  }, [refresh]);

  const rename = useCallback(async (wid, name) => {
    await authAxios.put(`${API}/workspaces/${wid}`, { name });
    await refresh();
  }, [refresh]);

  const remove = useCallback(async (wid) => {
    await authAxios.delete(`${API}/workspaces/${wid}`);
    if (wid === activeId) {
      const next = workspaces.find((w) => w.id !== wid);
      if (next) {
        setActiveId(next.id);
        try { localStorage.setItem(STORAGE_KEY, next.id); } catch { /* ignore */ }
      }
    }
    await refresh();
  }, [activeId, workspaces, refresh]);

  const active = useMemo(() => workspaces.find((w) => w.id === activeId) || workspaces[0] || null, [workspaces, activeId]);

  // The /auth/permissions endpoint reads X-Workspace-Id, so when the workspace
  // changes we need to refresh perms. We piggyback on the hard-reload in switchTo.
  // permissions exposed for read-only reference here.

  const value = useMemo(() => ({
    workspaces, active, max, loading: loading || authLoading, refresh, switchTo, createOne, rename, remove, permissions,
  }), [workspaces, active, max, loading, authLoading, refresh, switchTo, createOne, rename, remove, permissions]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useWorkspaces = () => useContext(Ctx);
